import React, { useState, useRef, useEffect } from 'react';
import { useLocaleContext } from '../context/LocaleContext';
import { ChevronDown, Check } from 'lucide-react';
import type { Locale } from '../types/locale';

const LanguageToggle: React.FC = () => {
  const { locale, changeLocale } = useLocaleContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: Array<{ code: Locale; flag: string; name: string }> = [
    { code: 'en', flag: '🇬🇧', name: 'English' },
    { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
    { code: 'pt-BR', flag: '🇧🇷', name: 'Português' },
    { code: 'ms-MY', flag: '🇲🇾', name: 'Bahasa Melayu' },
    { code: 'es', flag: '🇪🇸', name: 'Español' },
    { code: 'fr', flag: '🇫🇷', name: 'Français' },
    { code: 'ja', flag: '🇯🇵', name: '日本語' },
    { code: 'id', flag: '🇮🇩', name: 'Bahasa Indonesia' },
  ];

  const currentLanguage = languages.find(lang => lang.code === locale) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode: Locale) => {
    changeLocale(langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      {/* CLS FIX: min-w prevents width changes when locale name length varies */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-300 sm:min-w-[140px]"
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="text-base">{currentLanguage.flag}</span>
        <span className="hidden sm:inline">{currentLanguage.name}</span>
        <span className="sm:hidden">{currentLanguage.code.toUpperCase()}</span>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50 animate-fadeIn">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              aria-label={`Select language: ${lang.name}`}
              aria-current={locale === lang.code ? 'true' : undefined}
              className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                locale === lang.code
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-lg" aria-hidden="true">{lang.flag}</span>
                <span>{lang.name}</span>
              </div>
              {locale === lang.code && (
                <Check size={16} className="text-accent-600 dark:text-accent-400" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      )}

    </div>
  );
};

export default LanguageToggle;
